import { IAssetTypeGroup } from "../services/assetTypeGroupsService";
import { Device } from "../../../model/Device";
import { IInspectionProgress } from "../utils/inspectionProgressCalculation";
import { IPhotoValidationResult } from "../utils/photoValidationTypes";

/**
 * Unified asset type used throughout the inspection module.
 * Holds raw device data; formatting happens in the UI layer.
 */
export interface IInspectionAsset {
    /** Complete device information from API */
    device: Device;

    /** Display name of the asset type (dynamically loaded from backend Groups). Not unique — never match on it. */
    assetType: string;

    /**
     * ID of the asset-type group the device belongs to. Identifies the asset type; use it for any
     * comparison or lookup. Optional only because assets persisted by older builds lack it — read it
     * through getAssetTypeId(), which falls back to the device's groups.
     */
    assetTypeId?: string;

    /** Distance from current location in kilometers (raw number, formatted in UI) */
    distance?: number;

    /** Complete inspection log data (includes submittedDateTime, reviewedBy, etc.) */
    inspectionLog?: IInspectionLog;

    /** Template/checklist used for this inspection (includes id, name, and full parts when available) */
    inspectionTemplate?: IInspectionTemplateDetails;

    /** Full name of the user who reviewed this inspection */
    reviewedByName?: string;

    /** Full name of the user who submitted this inspection */
    submittedByName?: string;

    /** Item-level progress for incomplete inspections (Draft status) */
    inspectionProgress?: IInspectionProgress;

    /** Inspection type for vehicles and trailers (PreTrip, MidTrip, PostTrip, Other) */
    inspectionType?: HosLogType;

    /** Selected inspection type ID (from IInspectionType.id) */
    inspectionTypeId?: string;

    /** Mandatory context note when inspectionType is "Other" */
    inspectionTypeNote?: string;

    /** Active inspection draft data (in-progress inspection form data) */
    activeInspection?: IActiveInspection;

    /** Unrepaired defects for this asset (populated on asset list load) */
    unrepairedDefects?: IInspectedItem[];

    /**
     * IDs of InspectedItems the driver chose "Defect Not Corrected" for.
     * After the new InspectionLog is created these are linked via the LinkExistingInspectedItems API.
     */
    pendingCarryForwardDefectIds?: string[];

    /**
     * IDs of admin-repaired InspectedItems the driver denied ("Still defective") in the repaired
     * defect review. These are ALSO in pendingCarryForwardDefectIds (the old item is
     * linked to the new log — no new defect is created); this list additionally drives the
     * post-submission re-activation (itemStatus Repaired → Reported) so the driver stops being
     * asked to verify the repair and the defect returns to the admin portal as unresolved.
     */
    pendingReflagDefectIds?: string[];

    /**
     * Template-level inspection item IDs (InspectionItem group IDs) for the carried-forward defects.
     * Stored alongside pendingCarryForwardDefectIds so inspectionDetails can exclude them from
     * SaveInspectedSection without having to re-derive them from unrepairedDefects.
     */
    pendingCarryForwardTemplateItemIds?: string[];

    /** Most recent submitted log for this asset; the asset card shows its submitted date. */
    lastSubmittedLog?: IInspectionLog;

    /**
     * A completed inspection of the chosen template that the driver may continue from, found when
     * they committed to a template and type on the create page. Set only while the recertification
     * prompt is deciding what to do with it, and cleared on either choice.
     */
    recertifiableLog?: IInspectionLog;

    /**
     * Media already on the server that a recertification carried onto this inspection — item photos
     * and walkaround captures alike.
     *
     * Submission decides what to upload by diffing against the media on `inspectionLog`, and a
     * recertification deliberately has none: it is a new record, not an edit. Without this the
     * carried ids look like fresh local captures, and submission tries to read files that were never
     * on this device — a single such failure aborts the whole submission.
     */
    carriedUploadedMediaIds?: string[];
    /** Session ID shared with a paired trailer inspection. Null for single-asset inspections. */
    inspectionSessionId?: string;
}

/** Basic template info; IInspectionTemplateDetails adds the nested structure. */
export interface IInspectionTemplate {
    id: string;
    name: string;
    isArchived?: boolean;
}

export enum InspectionSignatureOption {
    None = "None",
    Optional = "Optional",
    Required = "Required"
}

/**
 * Template-level overall photo slot (e.g. "Front", "Left Side").
 * Configured by admins; rendered for the driver as the Asset Condition section.
 */
export interface IInspectionPhotoSlot {
    id: string;
    photoLabel?: string;
    photoRequired: boolean;
    sequence: number;
}

/**
 * Log-level walkaround photo capture.
 * Links a template photo slot (InspectionPhotoSlot.id) to the captured MediaFile on an InspectionLog.
 * Matches the backend InspectionLogPhotoSlot entity shape returned by the InspectionLog Get API.
 */
export interface IInspectionLogPhotoSlot {
    id?: string;
    inspectionPhotoSlot?: { id: string };
    mediaFile?: { id: string };
}

/** Full template with parts and items; the standard Get API returns the full nested structure. */
export interface IInspectionTemplateDetails extends IInspectionTemplate {
    inspectionParts?: IInspectionPart[];
    overallPhotoSlots?: IInspectionPhotoSlot[];
    signatureOption?: InspectionSignatureOption;
    signatureLabel?: string | null;
    captureLocation?: boolean;
    /** Whether driver certification is required. true/null → show certification step; false → hide it. */
    certificationRequired?: boolean | null;
    /**
     * Whether a driver may start an inspection from the asset's most recent one instead of an empty
     * checklist. Opt-in: null reads as false (unlike certificationRequired, where null reads as true).
     */
    allowRecertification?: boolean | null;
    /**
     * How far back the driver may reach for the inspection being recertified, in whole calendar days
     * ending today (1 = completed today). null means the default of 1.
     */
    recertificationWindowDays?: number | null;
    /** When true, disables the "Mark All as Pass" shortcut, requiring drivers to answer every item individually. null and false are both treated as off. */
    disablePassAll?: boolean | null;
    /**
     * When true, inspections also show the myGeotab adjusted (offset) odometer reading, read-only, alongside the raw one that is
     * always captured. It is display-only and is not saved on the log. null and false both show the raw reading alone.
     */
    captureAdjustedOdometer?: boolean | null;
}

/** A template section ("part"). */
export interface IInspectionPart {
    id: string;
    name: string;
    sequence?: number;
    inspectionItems?: IInspectionItem[];
}

export type InspectionItemType = "CheckItem" | "CustomProperty" | "SignatureItem" | "OtherItem";

export type InspectionItemOption = "NotNeeded" | "Optional" | "Required" | "RequiredWithDefect";

/** Inspection item from template. */
export interface IInspectionItem {
    id: string;
    name: string;
    sequence?: number;
    typeOfInput?: "String" | "Boolean" | "Integer";
    itemSeverity?: string[];
    noteOption?: InspectionItemOption;
    imageOption?: InspectionItemOption;
    itemType: InspectionItemType;
    /** Administrator-set; meaningful on CheckItem and CustomProperty only (see regulatoryInspection). */
    isRegulated?: boolean;
    isHidden?: boolean;
    /** Resolved Property object when itemType is "CustomProperty" (populated by backend on read) */
    customProperty?: {
        id: string;
        name?: string;
        propertyType?: string;
        entityTypes?: string[];
        externalReference?: string;
        isVisible?: boolean;
        minLength?: number;
        maxLength?: number;
        /**
         * propertySet.applicableAssetTypeGroups is populated by the backend with every asset-type group
         * a device can be a member of while still being in scope for this custom property — the group
         * the PropertySet is scoped to, its ancestors below the Asset Type root, and its descendants.
         * Customer-defined sub-groups (Asset Type > Vehicle > Heavy) are included alongside the built-in
         * ones, so a PropertySet scoped to "Heavy" arrives as {Vehicle, Heavy}.
         *
         * Pre-expanding the set that way lets the Drive app decide with a plain ID intersection against
         * the device's own groups (see isCustomPropertyApplicableToDevice): it is offline during an
         * inspection and cannot resolve group ancestry itself, and a device only ever belongs to the
         * groups an admin actually assigned, never to their ancestors.
         *
         * Absent or empty means no asset-type restriction — show the property for every asset.
         */
        propertySet?: { id: string; applicableAssetTypeGroups?: { id: string }[] };
        version?: string;
    };
}

/**
 * Per-item defect data entered by the user
 */
export interface IInspectionItemData {
    hasDefect?: boolean;
    notes: string;
    severity: string;
    signature: string;
    value: string;
    /** mediaId values from the native camera plugin (= the MediaFile entity ID on the server).
     * Populated at capture time; uploaded to the server on inspection submission. */
    images: string[];
}

/** Per-section data entered by the user. */
export interface IInspectionSectionData {
    noDefects: boolean;
    signature: string;
    items: { [itemId: string]: IInspectionItemData };
}

/** A configurable inspection type (e.g., Pre-Trip, Post-Trip, Other, or custom types). */
export interface IInspectionType {
    /** Unique identifier */
    id: string;
    /** Display name (e.g., "Pre-Trip", "Quarterly Maintenance") */
    name: string;
    /** True for pre-seeded default types */
    isDefault: boolean;
    /** True for the "Other" type that requires mandatory context note */
    isOther: boolean;
    /** True for regulatory types that trigger HOS On-Duty log creation */
    isRegulatory: boolean;
    /** HOS log comment for regulatory types (e.g., "PreTrip", "PostTrip"), null otherwise */
    hosLogComment: string | null;
}

export type HosLogType = "PreTrip" | "MidTrip" | "PostTrip" | "Other";

/** In-progress form data for an inspection being filled out. */
export interface IActiveInspection {
    sectionDataMap: { [sectionId: string]: IInspectionSectionData };
    /**
     * Inspection photo slot captures, keyed by InspectionPhotoSlot.id → mediaId from the native camera plugin.
     * Restored when resuming a draft, and carried forward by a recertification, on the same
     * reasoning as the item photos: the driver is confirming the asset is in the state the previous
     * inspection recorded.
     */
    overallPhotoSlotData?: { [slotId: string]: string };
    /** Capture-time photo validation results keyed by mediaId; restored with the draft. */
    photoValidation?: { [mediaId: string]: IPhotoValidationResult };
    /** ISO timestamp captured when the inspection was started, used for submission */
    startDateTime?: string;
    /**
     * ISO timestamp captured at the start of the current editing session (every mount, including
     * resume). Unlike startDateTime — which is anchored to the log's createdDateTime for the server
     * recorded-start and HOS logging — this measures only the active session so duration excludes
     * Save-for-Later idle gaps.
     */
    sessionStartDateTime?: string;
    /**
     * Location captured at inspection start when the template's captureLocation flag is true.
     * The coordinate is the GPS reading (preserved unchanged even if the driver overrides
     * the address text). formattedAddress is what the driver sees and may edit.
     */
    inspectionLocation?: IInspectionLocation;
    /**
     * Asset and inspector locations captured when this editing session started. Stored together with
     * sessionStartDateTime on Review & Sign so returning from Review reuses it; cleared when a saved inspection is
     * reopened so a new session captures again.
     */
    locationCapture?: IInspectionLocationCapture;
    /** Odometer reading in meters (auto-populated from device telemetry, editable by driver). */
    odometer?: number;
    /** Engine hours in seconds (auto-populated from device telemetry, editable by driver). */
    engineHours?: number;
    /** Load height in meters (for trailers, auto-populated when available, editable by driver). */
    loadHeight?: number;
    /** Load width in meters (for trailers, auto-populated when available, editable by driver). */
    loadWidth?: number;
}

/** Mirrors the server's InspectorDistanceStatus enum (sent and received by name). */
export type InspectorDistanceStatus =
    | "Succeeded"
    | "Disallowed"
    | "AssetLocationFailed"
    | "InspectorLocationFailed"
    | "InspectorLocationFromManualAddress"
    | "BothFailed";

/**
 * Result of capturing the asset location (device GPS only) and the inspector location (phone GPS only) at the start
 * of an inspection. driverLocation may instead hold a point geocoded from a manually entered address, in which case
 * status is InspectorLocationFromManualAddress (or stays BothFailed when the asset location also failed).
 */
export interface IInspectionLocationCapture {
    status: InspectorDistanceStatus;
    assetLocation?: ICoordinate;
    driverLocation?: ICoordinate;
}

/**
 * Captured inspection location.
 * - `location` is the GPS coordinate when capture succeeded. Preserved unchanged even if the
 *   driver overrides the address text — the GPS reading is the audit truth. Absent if capture
 *   failed and the driver typed an address manually.
 * - `formattedAddress` is what the driver sees on the form and what's saved with the log.
 *   Either the reverse-geocoded address or the driver's manual entry.
 */
export interface IInspectionLocation {
    location?: ICoordinate;
    formattedAddress: string;
}

/** Inspection Log entity from backend; only the properties the frontend uses. */
export interface IInspectionLog {
    id: string;
    asset?: { id: string };
    inspectionTemplate?: { id: string };
    logType?: string;
    logStatus?: string;
    createdDateTime?: string;
    submittedDateTime?: string;
    lastEditedDateTime?: string;
    reviewedBy?: { id: string };
    submittedBy?: { id: string; firstName?: string; lastName?: string; name?: string };
    inspectedItems?: IInspectedItem[];
    /**
     * Walkaround photo captures for this log. Each entry links a template photo slot
     * (InspectionPhotoSlot.id) to the captured MediaFile. Populated by the backend on read
     * and persisted on submit/save-for-later.
     */
    inspectionLogPhotoSlots?: IInspectionLogPhotoSlot[];
    location?: IAddressLookupData;
    /** Inspector's phone location when the inspection started (or geocoded from a manual address). */
    inspectorLocation?: ICoordinate;
    /** Outcome of the asset/inspector location capture; absent on logs from before the capture existed. */
    inspectorDistanceStatus?: InspectorDistanceStatus;
    /** Server-calculated distance in meters between location and inspectorLocation. */
    inspectorDistance?: number;
    /** Whether the asset was certified as safe to operate. */
    isSafeToOperate?: boolean;
    /** Links vehicle and trailer inspection logs created in the same session. */
    inspectionSessionId?: string;
    /** Odometer reading in meters (auto-populated from device telemetry, editable by driver). */
    odometer?: number;
    /** Engine hours in seconds (auto-populated from device telemetry, editable by driver). */
    engineHours?: number;
    /** Load height in meters (for trailers, auto-populated when available, editable by driver). */
    loadHeight?: number;
    /** Load width in meters (for trailers, auto-populated when available, editable by driver). */
    loadWidth?: number;
    /** Selected inspection type ID (from IInspectionType) */
    inspectionTypeId?: string;
    /** Full inspection type object returned by backend. */
    inspectionType?: IInspectionType;
    /** Mandatory context note when inspection type has isOther = true */
    inspectionTypeNote?: string;
    /** Carrier/authority name for regulatory inspections. */
    authorityName?: string;
    /** Carrier/authority address for regulatory inspections. */
    authorityAddress?: string;
    /**
     * Accumulated active inspection duration as HH:MM:SS. Written on Save for Later to persist
     * time across sessions; read on resume to add prior time to the current session on final submit.
     */
    duration?: string;
    /** User who performed the inspection (driver). */
    createdBy?: { id: string };
    /** User who certified the inspection (driver or mechanic, per template workflow). */
    certifiedBy?: { id: string };
    /** ISO timestamp of the certification. */
    certifyDate?: string;
    /** Optional remark entered at certification time. */
    certifyRemark?: string;
    /** Signature image captured at certification, stored as a MediaFile. */
    signatureMediaFile?: { id: string };
}

/** Flat unrepaired-defect row for the review panel, built from IInspectedItem plus section/template name lookups. */
export interface IUnrepairedDefectItem {
    id: string;
    sectionName: string;
    componentName: string;
    description: string;
    severity: string;
}

/** Known values of the InspectedItemStatus server enum. @see IInspectedItem.itemStatus */
export type InspectedItemStatus =
    | "Unknown" | "Pass" | "Fail" | "Reported" | "InProgress"
    | "Closed" | "Monitoring" | "Repaired" | "NotRepaired"
    | "RepairNotNecessary" | "Invalid";

/** Inspected item from the InspectionLog API response; references its template item by inspectionItem.id. */
export interface IInspectedItem {
    id?: string;
    inspectionItem?: { id: string };
    itemSeverity?: string;   // "Major" | "Minor" | "Cosmetic"
    itemStatus?: string;     // known values: InspectedItemStatus; kept as string to accept unrecognized API values
    itemNote?: string;
    itemValue?: string;
    repairStatus?: string;   // "NotRepaired" | "Repaired" | "RepairDisputed" | "NotNecessary"
    /**
     * Workflow the repair was actioned through: "Admin" (MyGeotab web portal) or "Driver"
     * (Drive outstanding-defects review). Absent when unknown — rows pre-dating the field,
     * or repairs recorded through a path that does not supply an origin.
     */
    repairSource?: string;
    /** Free-text note entered when marking a defect as repaired. */
    repairNote?: string;
    /** ISO timestamp of the repair, set when the defect was marked repaired. */
    repairDateTime?: string;
    /** User who performed/certified the repair. May carry only an id on read. */
    repairBy?: { id: string; firstName?: string; lastName?: string; name?: string };
    /**
     * Resolved display name of repairBy — UI-only enrichment so the history view can show
     * who resolved each defect without every row doing its own user lookup.
     */
    repairByName?: string;
    /** Reason code for a defect status change, e.g. "RepairedByMechanic", "RepairedByDriver", "RepairVerified", "DefectStillPresent". */
    statusChangeReason?: string;
    /** Free-text justification for a defect status change. The backend takes it from each request only. */
    statusChangeComments?: string;
    mediaFiles?: { id: string; thumbnails?: { id: string }[] }[];
    // Populated by GetInspectedItemsAsync — not present on items embedded in InspectionLog
    inspectionItemName?: string;  // e.g., "Wheels & Rims"
    inspectionPartName?: string;  // e.g., "Exterior"
    originalReportDateTime?: string;
    reportedBy?: string;
}

/** Inspected item enriched with the template's item name and sequence. UI-only; not returned by the API. */
export interface IInspectionHistoryItem extends IInspectedItem {
    itemName: string;
    itemSequence: number;
    /** Regulation state joined from the inspection template when grouping history details. */
    isRegulated?: boolean;
}

/** A section with its inspection results, for the history detail view. */
export interface IInspectionHistorySection {
    id: string;
    name: string;
    sequence?: number;
    inspectedItems: IInspectionHistoryItem[];
}

/**
 * Trailer info stored after a vehicle inspection so inspectionSubmitted.tsx
 * can prompt the driver to optionally inspect the attached trailer.
 */
export interface IPendingTrailerForSubmitted {
    vehicleLogId: string;
    trailer: { id: string; name: string; groups: { id: string }[] };
    inspectionType?: HosLogType;
    inspectionTypeId?: string;
}

/** Redux store state for the inspection module. */
export interface IInspectionStore {
    availableAssetTypes: IAssetTypeGroup[];
    /**
     * Asset-type groups whose inspections can be regulatory — the Vehicle/Trailer built-ins and
     * anything beneath them. Fetched once alongside availableAssetTypes; kept as a
     * separate field because the parent/child relationship is not available on IAssetTypeGroup.
     *
     * An array, not a Set: the store is exposed through DeepReadonly, which turns a Set into
     * DeepReadonlyObject<ReadonlySet<string>> and deep-readonlys its methods too, leaving them
     * uncallable. Consumers build the Set (see useSelectedAssetType).
     */
    regulatoryAssetTypeIds: string[];
    selectedAsset: IInspectionAsset | null;           // Assets tab → Create inspection flow & Incomplete tab → Resume incomplete flow
    selectedHistoryAsset: IInspectionAsset | null;    // History tab → View details flow
    /** Set after vehicle inspection submit when a trailer is attached; cleared when driver chooses inspect or skip. */
    pendingTrailerForSubmitted?: IPendingTrailerForSubmitted;
    /** Available inspection types fetched from API (with offline fallback to defaults) */
    inspectionTypes: IInspectionType[];
    /** True when inspection types have been loaded (successful or fallback) */
    inspectionTypesLoaded: boolean;
}

export enum InspectionActionTypes {
    SET_AVAILABLE_ASSET_TYPES = "SET_AVAILABLE_ASSET_TYPES",
    SET_REGULATORY_ASSET_TYPE_IDS = "SET_REGULATORY_ASSET_TYPE_IDS",
    SET_SELECTED_ASSET = "SET_SELECTED_ASSET",
    SET_SELECTED_HISTORY_ASSET = "SET_SELECTED_HISTORY_ASSET",
    SET_ACTIVE_INSPECTION = "SET_ACTIVE_INSPECTION",
    CLEAR_INSPECTION_STATE = "CLEAR_INSPECTION_STATE",
    SET_PENDING_TRAILER_FOR_SUBMITTED = "SET_PENDING_TRAILER_FOR_SUBMITTED",
    FETCH_INSPECTION_TYPES_REQUEST = "FETCH_INSPECTION_TYPES_REQUEST",
    FETCH_INSPECTION_TYPES_SUCCESS = "FETCH_INSPECTION_TYPES_SUCCESS"
}
